'use client'

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase-browser'
import { resolveAvailabilityTier, resolveBundleAvailability } from '@sifly/shared-utils'
import type { SkuAvailabilityRow } from '@sifly/shared-types'
import type { CartLine } from '@/lib/cart'

type CartLineStatus = 'ok' | 'out_of_stock' | 'unknown'

type CartAvailabilityResult = {
  statusBySkuId: Record<string, CartLineStatus>
  rowBySkuId: Record<string, SkuAvailabilityRow>
  /** For each bundle line in cart (SiFly or dealer), its aggregated status
   *  keyed on the line's lineId. */
  bundleStatusByLineId: Record<string, CartLineStatus>
}

/**
 * Resolve per-line availability for the cart. Covers:
 *   - SiFly-source simple SKUs (master skus.id → get_sku_availability)
 *   - SiFly-source bundles (option SKUs aggregated via resolveBundleAvailability,
 *     worst tier wins)
 *   - Dealer-source simple SKUs (dealer_skus.id → get_dealer_sku_availability,
 *     synthesized into a SkuAvailabilityRow with the dealer's qty stored in
 *     dealer_local_qty so the same tier resolver applies)
 *   - Dealer-source bundles (option dealer_sku_ids aggregated via
 *     resolveBundleAvailability over synthesized rows, worst tier wins)
 *
 * Returns:
 *  - statusBySkuId:      simple-line status keyed on item.skuId (whichever source)
 *  - rowBySkuId:         raw availability rows for per-tier overflow disclosures
 *                        (dealer rows have usa_qty=0, bulgaria_qty=0)
 *  - bundleStatusByLineId: bundle-line status keyed on the line's lineId
 *
 * Re-fetches whenever the cart's set of relevant IDs changes OR `nonce`
 * changes — callers bump nonce on drawer open to force re-check.
 */
export function useCartAvailability(
  items: CartLine[],
  tenantId: string,
  nonce = 0,
): CartAvailabilityResult {
  const [result, setResult] = useState<CartAvailabilityResult>({
    statusBySkuId: {},
    rowBySkuId: {},
    bundleStatusByLineId: {},
  })

  // Simple SiFly lines (master skus.id).
  const simpleSiflySkuIds = items
    .filter((i) => i.source === 'sifly' && i.productType !== 'bundle' && i.skuId)
    .map((i) => i.skuId as string)

  // Simple dealer lines (dealer_skus.id).
  const simpleDealerSkuIds = items
    .filter((i) => i.source === 'dealer' && i.productType !== 'bundle' && i.skuId)
    .map((i) => i.skuId as string)

  // SiFly bundle lines — collect their option IDs so we can look up
  // component sku_ids before resolving availability.
  const bundleOptionIds = items
    .filter((i) => i.source === 'sifly' && i.productType === 'bundle' && i.bundleOptionIds?.length)
    .flatMap((i) => i.bundleOptionIds ?? [])

  // Dealer bundle lines — their bundleOptionIds reference
  // dealer_bundle_slot_options.id, which resolve to dealer_sku_id.
  const dealerBundleOptionIds = items
    .filter((i) => i.source === 'dealer' && i.productType === 'bundle' && i.bundleOptionIds?.length)
    .flatMap((i) => i.bundleOptionIds ?? [])

  const simpleSiflyKey = [...new Set(simpleSiflySkuIds)].sort().join(',')
  const simpleDealerKey = [...new Set(simpleDealerSkuIds)].sort().join(',')
  const bundleKey = [...new Set(bundleOptionIds)].sort().join(',')
  const dealerBundleKey = [...new Set(dealerBundleOptionIds)].sort().join(',')

  useEffect(() => {
    let cancelled = false
    if (!tenantId || (!simpleSiflyKey && !simpleDealerKey && !bundleKey && !dealerBundleKey)) {
      setResult({ statusBySkuId: {}, rowBySkuId: {}, bundleStatusByLineId: {} })
      return
    }

    ;(async () => {
      const statusBySkuId: Record<string, CartLineStatus> = {}
      const rowBySkuId: Record<string, SkuAvailabilityRow> = {}

      // Step 1: resolve any SiFly bundle option IDs to their underlying sku_ids.
      const optionToSku: Record<string, string> = {}
      if (bundleKey) {
        const { data: optRows } = await supabase
          .from('bundle_slot_options')
          .select('id, sku_id')
          .in('id', bundleKey.split(','))
        if (Array.isArray(optRows)) {
          for (const row of optRows as Array<{ id: string; sku_id: string }>) {
            optionToSku[row.id] = row.sku_id
          }
        }
      }

      // Step 1b: resolve dealer-bundle option IDs to dealer_sku_id.
      const dealerOptionToDealerSku: Record<string, string> = {}
      if (dealerBundleKey) {
        const { data: optRows } = await supabase
          .from('dealer_bundle_slot_options')
          .select('id, dealer_sku_id')
          .in('id', dealerBundleKey.split(','))
        if (Array.isArray(optRows)) {
          for (const row of optRows as Array<{ id: string; dealer_sku_id: string }>) {
            dealerOptionToDealerSku[row.id] = row.dealer_sku_id
          }
        }
      }

      // Step 2a: master-SKU availability (SiFly simples + SiFly bundle components).
      const masterSkuIds = new Set<string>()
      for (const id of simpleSiflyKey ? simpleSiflyKey.split(',') : []) masterSkuIds.add(id)
      for (const skuId of Object.values(optionToSku)) masterSkuIds.add(skuId)

      if (masterSkuIds.size > 0) {
        const { data, error } = await supabase.rpc('get_sku_availability', {
          p_tenant_id: tenantId,
          p_sku_ids: Array.from(masterSkuIds),
        })
        if (cancelled) return
        if (!error && Array.isArray(data)) {
          for (const row of data as SkuAvailabilityRow[]) {
            const tier = resolveAvailabilityTier(row).tier
            statusBySkuId[row.sku_id] = tier === 'out_of_stock' ? 'out_of_stock' : 'ok'
            rowBySkuId[row.sku_id] = row
          }
        }
      }

      // Step 2b: dealer-custom availability. RPC returns a flat shape
      // (dealer_sku_id, track_inventory, is_active, qty) — synthesize a
      // SkuAvailabilityRow so resolveAvailabilityTier and the cart drawer's
      // overflow logic continue to work with one code path. The dealer qty
      // is stored in dealer_local_qty (dealer-custom has only one "pool"),
      // with usa/bulgaria zeroed — explainQtyAcrossPools then never shows
      // an overflow disclosure for dealer lines, which is correct.
      // Fold dealer-bundle component dealer_sku_ids into the same call so
      // bundles and simples share one RPC roundtrip.
      const dealerIdsSet = new Set<string>()
      if (simpleDealerKey) for (const id of simpleDealerKey.split(',')) dealerIdsSet.add(id)
      for (const dsku of Object.values(dealerOptionToDealerSku)) dealerIdsSet.add(dsku)

      if (dealerIdsSet.size > 0) {
        const dealerIds = Array.from(dealerIdsSet)
        const { data, error } = await supabase.rpc('get_dealer_sku_availability', {
          p_tenant_id: tenantId,
          p_dealer_sku_ids: dealerIds,
        })
        if (cancelled) return
        if (!error && Array.isArray(data)) {
          type DealerRow = {
            dealer_sku_id: string
            track_inventory: boolean
            is_active: boolean
            qty: number
          }
          for (const r of data as DealerRow[]) {
            const synth: SkuAvailabilityRow = {
              sku_id: r.dealer_sku_id,
              track_inventory: r.track_inventory,
              is_active: r.is_active,
              dealer_local_qty: r.qty,
              usa_qty: 0,
              bulgaria_qty: 0,
            }
            const tier = resolveAvailabilityTier(synth).tier
            statusBySkuId[r.dealer_sku_id] = tier === 'out_of_stock' ? 'out_of_stock' : 'ok'
            rowBySkuId[r.dealer_sku_id] = synth
          }
        }
      }

      // Step 3: aggregate each bundle item's status from its component rows.
      // SiFly bundles map option_id → master sku_id; dealer bundles map
      // option_id → dealer_sku_id. Both resolve into rowBySkuId (dealer rows
      // are synthesized SkuAvailabilityRows in Step 2b), so the same
      // resolveBundleAvailability call handles both.
      const bundleStatusByLineId: Record<string, CartLineStatus> = {}
      for (const item of items) {
        if (item.productType !== 'bundle' || !item.bundleOptionIds?.length) continue
        const optMap = item.source === 'dealer' ? dealerOptionToDealerSku : optionToSku
        const componentRows = item.bundleOptionIds
          .map((optId) => optMap[optId])
          .filter(Boolean)
          .map((skuId) => rowBySkuId[skuId])
          .filter((r): r is SkuAvailabilityRow => !!r)
        if (componentRows.length === 0) {
          bundleStatusByLineId[item.lineId] = 'unknown'
          continue
        }
        const agg = resolveBundleAvailability(componentRows)
        bundleStatusByLineId[item.lineId] = agg.tier === 'out_of_stock' ? 'out_of_stock' : 'ok'
      }

      setResult({ statusBySkuId, rowBySkuId, bundleStatusByLineId })
    })()

    return () => {
      cancelled = true
    }
  }, [tenantId, simpleSiflyKey, simpleDealerKey, bundleKey, dealerBundleKey, nonce])

  return result
}

/**
 * Given a customer's desired qty + the SKU's pool availability, compute
 * how many units come from each pool. Pools drain in order:
 *   dealer_local → sifly_usa → sifly_bulgaria
 * Returns the breakdown; sum may be less than qty if pools combined < qty
 * (caller should treat any unfilled remainder as OOS).
 */
export function explainQtyAcrossPools(
  qty: number,
  row: SkuAvailabilityRow,
): { local: number; usa: number; bulgaria: number; unfilled: number } {
  let remaining = Math.max(0, qty)
  const local = Math.min(remaining, Math.max(0, row.dealer_local_qty))
  remaining -= local
  const usa = Math.min(remaining, Math.max(0, row.usa_qty))
  remaining -= usa
  const bulgaria = Math.min(remaining, Math.max(0, row.bulgaria_qty))
  remaining -= bulgaria
  return { local, usa, bulgaria, unfilled: remaining }
}
